import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { PhoneNumber } from '../phone/PhoneNumber';
import { AanFieldPicker, type Candidate } from '../inbox/AanFieldPicker';
import { formatFileSize } from '../inbox/AttachmentList';
import { MAX_MAIL_BIJLAGEN_BYTES, MAX_MAIL_BIJLAGEN_LABEL } from '../../lib/factuurconcept';
import {
  UITVOERENDE_MERKEN,
  bepaalUitvoerendMerk,
  herkomstToelichting,
  type UitvoerendMerk,
} from '../../lib/merk';

interface CustomerLite {
  id: string;
  name: string;
  email: string;
  phone: string;
}

interface OrderLite {
  brand?: string;
}

interface ContactLite {
  id: string;
  name: string;
  email: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Optioneel: zonder klant is de composer een vrije "nieuwe e-mail" (Aan-veld leeg/typbaar). */
  customer?: CustomerLite;
  /** Voorgevulde ontvanger. Valt terug op customer.email als leeg. */
  to?: string;
  /** Contactpersonen met e-mail, voor snelkeuze in het Aan-veld. */
  contacts?: ContactLite[];
  orders?: OrderLite[];
  prefillAanleiding?: string;
  /** Voorgeselecteerd merk als er geen orders zijn (bv. conversation.brand). */
  defaultBrand?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Zelfde poort als reply/forward: de echte Vercel-grens, base64 in de JSON-body. */
const MAX_TOTAL_ATTACHMENT_BYTES = MAX_MAIL_BIJLAGEN_BYTES;

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

/** Client-side spiegel van MAILBOXES in api/_lib/gmail.ts — alleen voor weergave van het vanaf-adres. */
const BRAND_MAILBOX: Record<UitvoerendMerk, string> = {
  KLEDA: 'info@kleda.nl',
  CINCO: 'info@cincocleaning.com',
  SCHOONHUISSHOP: 'info@schoonhuisshop.nl',
  SOFASAVER: 'info@sofasaver.nl',
};

export function OutboundComposerModal({ open, onClose, customer, to, contacts, orders, prefillAanleiding, defaultBrand: defaultBrandProp }: Props) {
  // Het merk op de order is herkomst en kan een satellietsite zijn
  // (VLOERKLEEDREINIGEN, BANKREINIGEN). Die heeft geen eigen mailbox, dus we
  // schakelen op de uitvoerder. De herkomst tonen we ernaast, zodat kantoor
  // ziet waar de klant vandaan komt.
  const herkomstMerk = orders?.[0]?.brand || defaultBrandProp || null;
  const defaultBrand = bepaalUitvoerendMerk({ herkomstMerk });
  const herkomst = herkomstToelichting(herkomstMerk);

  // Contactpersonen van deze klant als vaste kandidaten in het Aan-veld: zij
  // staan bij focus meteen klaar, nog voor je iets getypt hebt.
  const contactCandidates = useMemo<Candidate[]>(
    () =>
      (contacts ?? [])
        .filter((c) => c.email?.includes('@'))
        .map((c) => ({ email: c.email, name: c.name || '', source: 'contact', score: 0 })),
    [contacts],
  );

  const [brand, setBrand] = useState<UitvoerendMerk>(defaultBrand);
  const [recipient, setRecipient] = useState<string>('');
  const [aanleiding, setAanleiding] = useState<string>(prefillAanleiding || '');
  const [subject, setSubject] = useState<string>('');
  const [bodyText, setBodyText] = useState<string>('');
  const [cc, setCc] = useState<string>('');
  const [bcc, setBcc] = useState<string>('');
  const [showCcBcc, setShowCcBcc] = useState<boolean>(false);
  const [files, setFiles] = useState<File[]>([]);
  const [filesError, setFilesError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [aiDraft, setAiDraft] = useState<string | null>(null);
  const [aiSubject, setAiSubject] = useState<string | null>(null);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [aiConfidence, setAiConfidence] = useState<number | null>(null);
  const [aiConfidenceReason, setAiConfidenceReason] = useState<string | null>(null);

  const [loadingDraft, setLoadingDraft] = useState(false);
  const [loadingSend, setLoadingSend] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state on open
  useEffect(() => {
    if (open) {
      setBrand(defaultBrand);
      setRecipient(to || customer?.email || '');
      setAanleiding(prefillAanleiding || '');
      setSubject('');
      setBodyText('');
      setCc('');
      setBcc('');
      setShowCcBcc(false);
      setFiles([]);
      setFilesError(null);
      setAiDraft(null);
      setAiSubject(null);
      setAiAnalysis(null);
      setAiConfidence(null);
      setAiConfidenceReason(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const totalFileBytes = files.reduce((n, f) => n + f.size, 0);

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;
    const next = [...files, ...picked];
    const total = next.reduce((n, f) => n + f.size, 0);
    if (total > MAX_TOTAL_ATTACHMENT_BYTES) {
      setFilesError(`Bijlagen zijn samen ${(total / 1024 / 1024).toFixed(1)} MB, maximaal ${MAX_MAIL_BIJLAGEN_LABEL}. Verklein de foto of stuur hem los.`);
      return;
    }
    setFilesError(null);
    setFiles(next);
  }

  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setFilesError(null);
  }

  async function authHeaders(): Promise<Record<string, string>> {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token || '';
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  async function handleGenerate() {
    if (!aanleiding.trim()) return;
    const contextEmail = recipient.trim() || customer?.email || '';
    const contextPhone = customer?.phone || '';
    if (!contextEmail && !contextPhone) {
      setError('Vul eerst een ontvanger in (Aan), dan kan de AI klantcontext ophalen.');
      return;
    }
    setLoadingDraft(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const res = await fetch('/api/outbound/draft', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          brand,
          // Herkomst apart meesturen: `brand` is hier al de uitvoerder, dus
          // zonder dit veld weet de AI niet meer dat de klant via een
          // satellietsite binnenkwam.
          herkomst_brand: herkomstMerk || undefined,
          customer_email: contextEmail || undefined,
          customer_phone: contextPhone || undefined,
          aanleiding,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setSubject(data.subject || '');
      setBodyText(data.draft || '');
      setAiDraft(data.draft || null);
      setAiSubject(data.subject || null);
      setAiAnalysis(data.analysis || null);
      setAiConfidence(typeof data.confidence === 'number' ? data.confidence : null);
      setAiConfidenceReason(data.confidence_reason || null);
    } catch (err: any) {
      setError(err?.message || 'Concept genereren mislukt');
    } finally {
      setLoadingDraft(false);
    }
  }

  async function handleSend() {
    const toClean = recipient.trim();
    if (!subject.trim() || !bodyText.trim() || !EMAIL_RE.test(toClean)) return;
    if (filesError) { setError(filesError); return; }
    setLoadingSend(true);
    setError(null);
    try {
      const headers = await authHeaders();
      const attachments = files.length
        ? await Promise.all(files.map(async (f) => ({
            filename: f.name,
            mimeType: f.type || 'application/octet-stream',
            dataBase64: await fileToBase64(f),
          })))
        : undefined;
      const res = await fetch('/api/outbound/send', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          brand,
          to: toClean,
          cc: cc.trim() || undefined,
          bcc: bcc.trim() || undefined,
          subject,
          body_text: bodyText,
          body_html: bodyText.replace(/\n/g, '<br/>'),
          customer_id: customer?.id || undefined,
          customer_name: customer?.name || undefined,
          ai_draft: aiDraft || undefined,
          ai_draft_subject: aiSubject || undefined,
          ai_draft_analysis: aiAnalysis || undefined,
          ai_draft_confidence: aiConfidence ?? undefined,
          ai_draft_confidence_reason: aiConfidenceReason || undefined,
          aanleiding: aanleiding || undefined,
          attachments,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (data.warning) {
        alert(`Verstuurd, maar: ${data.warning}`);
      } else {
        alert(`Verstuurd naar ${toClean}`);
      }
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Versturen mislukt');
    } finally {
      setLoadingSend(false);
    }
  }

  const sendDisabled = !subject.trim() || !bodyText.trim() || !EMAIL_RE.test(recipient.trim()) || loadingSend || !!filesError;

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {customer?.name ? `Stuur e-mail naar ${customer.name}` : 'Nieuwe e-mail'}
            </h2>
            {customer?.phone ? (
              <div className="text-xs text-slate-500 mt-1"><PhoneNumber number={customer.phone} customerName={customer?.name} sourceContext="outbound-composer" /></div>
            ) : null}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
            aria-label="Sluiten"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="mb-3 p-2.5 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
            {error}
          </div>
        )}

        <div className="mb-3">
          <label className="block text-xs font-medium text-slate-600 mb-1">Aan</label>
          {/* Was een native datalist met alleen de contactpersonen van deze
              klant. De picker houdt die snelkeuze (ze staan bij focus bovenaan)
              en zoekt daarnaast live in het adresboek: reparateurs, partners,
              klanten en eerdere correspondenten. */}
          <AanFieldPicker
            variant="stacked"
            candidates={contactCandidates}
            value={recipient}
            onChange={setRecipient}
          />
        </div>

        {/* Altijd zichtbaar: het merk bepaalt het vanaf-adres, en dat mag nooit onzichtbaar zijn.
            Detectie blijft: het gedetecteerde merk staat voorgeselecteerd via defaultBrand. */}
        <div className="mb-3">
          <label className="block text-xs font-medium text-slate-600 mb-1">
            Merk{BRAND_MAILBOX[brand] ? ` — verstuurt vanaf ${BRAND_MAILBOX[brand]}` : ''}
          </label>
          <select
            value={brand}
            onChange={(e) => setBrand(e.target.value as UitvoerendMerk)}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
          >
            {UITVOERENDE_MERKEN.map(b => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          {herkomst ? (
            <p className="mt-1 text-xs text-slate-500">
              Binnengekomen via {herkomst}. De uitvoering en de mail lopen via {brand}.
            </p>
          ) : null}
        </div>

        <div className="mb-3">
          <label className="block text-xs font-medium text-slate-600 mb-1">Aanleiding / context</label>
          <textarea
            value={aanleiding}
            onChange={(e) => setAanleiding(e.target.value)}
            rows={4}
            placeholder="Bijv. 'BizziPhone-belnotitie: klant wil weten of we ook bankhoezen reinigen.'"
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
          />
          <button
            onClick={handleGenerate}
            disabled={!aanleiding.trim() || loadingDraft}
            className="mt-2 px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            {loadingDraft ? 'Genereren...' : 'AI concept genereren'}
          </button>
        </div>

        {!showCcBcc && (
          <div className="mb-3">
            <button
              onClick={() => setShowCcBcc(true)}
              className="text-xs text-indigo-600 hover:text-indigo-700"
            >
              + CC / BCC toevoegen
            </button>
          </div>
        )}

        {showCcBcc && (
          <>
            <div className="mb-3">
              <label className="block text-xs font-medium text-slate-600 mb-1">CC</label>
              <input
                type="text"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="naam@voorbeeld.nl, ..."
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div className="mb-3">
              <label className="block text-xs font-medium text-slate-600 mb-1">BCC</label>
              <input
                type="text"
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
                placeholder="naam@voorbeeld.nl, ..."
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              />
            </div>
          </>
        )}

        <div className="mb-3">
          <label className="block text-xs font-medium text-slate-600 mb-1">Onderwerp</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
          />
        </div>

        <div className="mb-3">
          <label className="block text-xs font-medium text-slate-600 mb-1">Bericht</label>
          <textarea
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            rows={10}
            className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono"
          />
        </div>

        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium text-slate-600">Bijlagen</label>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-xs text-indigo-600 hover:text-indigo-700"
            >
              + Bestand toevoegen
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
          {files.length > 0 && (
            <div className="space-y-1.5">
              {files.map((f, idx) => (
                <div
                  key={`${f.name}-${idx}`}
                  className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 border border-slate-200 rounded text-sm"
                >
                  <span className="flex-1 truncate text-slate-700">{f.name}</span>
                  <span className="text-xs text-slate-400">{formatFileSize(f.size)}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(idx)}
                    className="text-slate-400 hover:text-rose-500"
                    title="Verwijderen"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              <p className="text-xs text-slate-400">
                Totaal {(totalFileBytes / 1024 / 1024).toFixed(1)} MB van 20 MB
              </p>
            </div>
          )}
          {filesError && <p className="text-xs text-rose-600 mt-1">{filesError}</p>}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
          >
            Annuleren
          </button>
          <button
            onClick={handleSend}
            disabled={sendDisabled}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
          >
            {loadingSend ? 'Versturen...' : 'Verstuur'}
          </button>
        </div>
      </div>
    </div>
  );
}
